import React, { useState } from "react";
import HttpService from "../services/HttpService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Box,
  Autocomplete,
  CircularProgress,
  Divider,
  ListSubheader,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import Labels from "../labels/Labels";
import { useChosenEmployer } from "../contexts/EmployerContext";

export default function InterCompanyTransfer() {
  const { chosenEmployerId } = useChosenEmployer();
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState({
    fromBalanceId: "",
    toBalanceId: "",
    amount: "",
    reference: "",
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch balances for source and destination selects
  const {
    data: balances,
    isLoading: balancesLoading,
    error: balancesError,
  } = useQuery(
    ["multiBalanceStats"],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + "/balance/multistats"
      );
      return response.data;
    },
    {
      onError: (error) => {
        console.error("Error fetching balances:", error);
      },
    }
  );

  const handleInputChange = (field, value) => {
    // Clear submit error when user starts making changes
    if (submitError) {
      setSubmitError("");
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      // Reset amount when source balance changes
      ...(field === "fromBalanceId" && { amount: "" }),
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }

    // Clear amount error when source balance changes
    if (field === "fromBalanceId" && errors.amount) {
      setErrors((prev) => ({
        ...prev,
        amount: null,
      }));
    }

    // Validate amount against source balance
    if (field === "amount" && selectedSourceBalance) {
      const numericValue = parseFloat(value);
      if (numericValue > selectedSourceBalance.currentBalance) {
        setErrors((prev) => ({
          ...prev,
          amount: `Amount cannot exceed available balance of ${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            }
          ).format(selectedSourceBalance.currentBalance)}`,
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);
    // Clear previous submit error
    setSubmitError("");

    // Final validation before submit
    if (selectedSourceBalance) {
      const numericAmount = parseFloat(formData.amount);
      if (numericAmount > selectedSourceBalance.currentBalance) {
        setErrors((prev) => ({
          ...prev,
          amount: `Amount cannot exceed available balance of ${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            }
          ).format(selectedSourceBalance.currentBalance)}`,
        }));
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await HttpService.getAxiosClient().post(
        window.API_URL + "/balance/transfer",
        formData
      );

      // Invalidate and refetch balance data
      await queryClient.invalidateQueries(["multiBalanceStats"]);

      // Reset form
      setFormData({
        fromBalanceId: "",
        toBalanceId: "",
        amount: "",
        reference: "",
      });
      // Clear errors
      setErrors({});
    } catch (error) {
      // Extract error message from API response
      let errorMessage = Labels.getLabel(
        "interCompanyTransfer.GenericError",
        "An error occurred while processing the transfer."
      );

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 403) {
          errorMessage = Labels.getLabel(
            "interCompanyTransfer.PermissionError",
            "You don't have permission to perform this transfer."
          );
        } else if (status === 400) {
          errorMessage =
            data.message ||
            Labels.getLabel(
              "interCompanyTransfer.InvalidRequest",
              "Invalid transfer request."
            );
        } else if (status === 500) {
          errorMessage = Labels.getLabel(
            "interCompanyTransfer.ServerError",
            "Server error. Please try again later."
          );
        } else if (data && data.message) {
          errorMessage = data.message;
        } else {
          errorMessage = `${Labels.getLabel(
            "interCompanyTransfer.Error",
            "Error"
          )} ${status}: ${
            error.response.statusText ||
            Labels.getLabel(
              "interCompanyTransfer.UnknownError",
              "Unknown error"
            )
          }`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = Labels.getLabel(
          "interCompanyTransfer.NetworkError",
          "Network error. Please check your connection and try again."
        );
      } else {
        // Other error
        errorMessage =
          error.message ||
          Labels.getLabel(
            "interCompanyTransfer.UnexpectedError",
            "An unexpected error occurred."
          );
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to identify employer balance
  const getEmployerBalance = () => {
    if (!Array.isArray(balances)) return null;
    const headerEmployerId = HttpService.getAxiosClient().defaults.headers['x-chosen-employer'];
    const targetId = chosenEmployerId ? String(chosenEmployerId) : (headerEmployerId ? String(headerEmployerId) : null);
    return balances.find((b) => String(b.idEmployer) === targetId);
  };

  const employerBalance = getEmployerBalance();

  const balanceOptions = Array.isArray(balances)
    ? balances.map((balance) => ({
        value: balance.idBalance,
        name: balance.employerDescription,
        balance: balance.currentBalance,
        isEmployer: employerBalance && balance.idBalance === employerBalance.idBalance,
      }))
    : [];

  // Sort: employer first, then alphabetically
  const sortedBalanceOptions = [...balanceOptions].sort((a, b) => {
    if (a.isEmployer) return -1;
    if (b.isEmployer) return 1;
    return a.name.localeCompare(b.name);
  });

  // Filter source balances to only show those with available funds
  const sourceBalanceOptions = sortedBalanceOptions.filter((option) => option.balance > 0);

  // Get selected source balance details
  const selectedSourceBalance = Array.isArray(balances)
    ? balances.find((balance) => balance.idBalance === formData.fromBalanceId)
    : null;

  return (
    <Container maxWidth="md">
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <h2>
            {Labels.getLabel(
              "interCompanyTransfer.Title",
              "Inter Company Transfer"
            )}
          </h2>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {balancesError && (
                <Typography color="error" gutterBottom>
                  {Labels.getLabel(
                    "interCompanyTransfer.ErrorLoadingBalances",
                    "Error loading balances. Please try again."
                  )}
                </Typography>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12}>
                    <Autocomplete
                      options={sourceBalanceOptions}
                      getOptionLabel={(option) => option.name}
                      value={
                        sourceBalanceOptions.find(
                          (opt) => opt.value === formData.fromBalanceId
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleInputChange(
                          "fromBalanceId",
                          newValue ? newValue.value : ""
                        );
                      }}
                      loading={balancesLoading}
                      disabled={balancesLoading}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderOption={(props, option) => {
                        const showDivider = option.isEmployer;
                        return (
                          <React.Fragment key={option.value}>
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                display: 'flex !important',
                                justifyContent: 'space-between !important',
                                alignItems: 'center !important',
                                width: '100%',
                                py: 1,
                              }}
                            >
                              <Typography variant="inherit">{option.name}</Typography>
                              <Typography
                                variant="inherit"
                                sx={{
                                  fontWeight: option.isEmployer ? 'bold' : 'normal',
                                  color: option.isEmployer ? 'primary.main' : 'text.secondary',
                                  ml: 2,
                                }}
                              >
                                {new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(option.balance)}
                              </Typography>
                            </Box>
                            {showDivider && <Divider component="li" />}
                          </React.Fragment>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Labels.getLabel(
                            "interCompanyTransfer.SourceBalance",
                            "Source Balance"
                          )}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {balancesLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Autocomplete
                      options={sortedBalanceOptions.filter(
                        (opt) => opt.value !== formData.fromBalanceId
                      )}
                      getOptionLabel={(option) => option.name}
                      value={
                        sortedBalanceOptions.find(
                          (opt) => opt.value === formData.toBalanceId
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleInputChange(
                          "toBalanceId",
                          newValue ? newValue.value : ""
                        );
                      }}
                      loading={balancesLoading}
                      disabled={balancesLoading}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderOption={(props, option) => {
                        const showDivider = option.isEmployer;
                        return (
                          <React.Fragment key={option.value}>
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                display: 'flex !important',
                                justifyContent: 'space-between !important',
                                alignItems: 'center !important',
                                width: '100%',
                                py: 1,
                              }}
                            >
                              <Typography variant="inherit">{option.name}</Typography>
                              <Typography
                                variant="inherit"
                                sx={{
                                  fontWeight: option.isEmployer ? 'bold' : 'normal',
                                  color: option.isEmployer ? 'primary.main' : 'text.secondary',
                                  ml: 2,
                                }}
                              >
                                {new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(option.balance)}
                              </Typography>
                            </Box>
                            {showDivider && <Divider component="li" />}
                          </React.Fragment>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Labels.getLabel(
                            "interCompanyTransfer.DestinationBalance",
                            "Destination Balance"
                          )}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {balancesLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <NumericFormat
                      fullWidth
                      label="Amount"
                      value={formData.amount}
                      onValueChange={(values) => {
                        const { value } = values;
                        handleInputChange("amount", value);
                      }}
                      thousandSeparator={true}
                      prefix={"$"}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      allowNegative={false}
                      customInput={TextField}
                      error={!!errors.amount}
                      helperText={errors.amount}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Reference"
                      value={formData.reference}
                      onChange={(e) =>
                        handleInputChange("reference", e.target.value)
                      }
                      placeholder="Optional reference"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={
                        !formData.fromBalanceId ||
                        !formData.toBalanceId ||
                        !formData.amount ||
                        !!errors.amount ||
                        isSubmitting
                      }
                    >
                      {isSubmitting
                        ? Labels.getLabel(
                            "interCompanyTransfer.Processing",
                            "Processing Transfer..."
                          )
                        : Labels.getLabel(
                            "interCompanyTransfer.Submit",
                            "Submit Transfer"
                          )}
                    </Button>
                    {submitError && (
                      <Typography color="error">{submitError}</Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
